import React, { Fragment, useContext } from "react"
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { SelectValidator, TextValidator } from 'react-material-ui-form-validator';
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import QaiChallengeViewModel from "./ViewModels/QaiChallengeViewModel";
import QaiScorecardViewModel from "./ViewModels/QaiScorecardViewModel";
import UserType from "../../Utilities/UserType";
import { APIContext } from "../../Contexts/APIContext";

interface IChallengeQuestionFormProps {
    view: UserType
    setOpenChallengeDialog: (value: boolean) => void;
    challengeViewModel: QaiChallengeViewModel
    setChallengeViewModel: (challengeViewModel: QaiChallengeViewModel) => void;
    scorecardViewModel: QaiScorecardViewModel | null
}

export default function ChallengeQuestionForm({
    view,
    setOpenChallengeDialog,
    challengeViewModel,
    setChallengeViewModel,
    scorecardViewModel
}: IChallengeQuestionFormProps) {
    const { loading } = useContext(APIContext);

    const onChangeHandler = (e: any) => {
        setChallengeViewModel({
            ...challengeViewModel,
            [e.target.name]: e.target.value
        })
    }

    const noteOnChangeHandler = (e: any) => {
        setChallengeViewModel({
            ...challengeViewModel,
            note: e.target.value
        });
    }

    return (
        !scorecardViewModel ?
            <Grid container justifyContent="center" alignItems="center" size={12}>
                <Grid>
                    <Typography>There was an error loading scorecards</Typography>
                </Grid>
                <Grid container size={12} justifyContent="flex-end" alignItems="flex-end">
                    <Grid>
                        <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            onClick={() => setOpenChallengeDialog(false)}>
                            Close
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
            :
            <Fragment>
                <Grid marginBottom={2} size={12}>
                    <Typography>This form is for challenging your result from the report of a specific question of the Q-AI generated scorecards. Each question can only be challenged once within a 7-day period.</Typography>
                </Grid>
                <Grid container justifyContent="space-between" height="300px">
                    <Grid
                        container
                        size={5.8}
                        justifyContent="space-between"
                        marginLeft={0}
                        marginTop={0}
                        marginBottom={2}
                        paddingTop="25px"
                        height="80%">
                        <Grid size={12} marginBottom={2}>
                            <SelectValidator
                                id="template"
                                name="templateId"
                                label="Template"
                                variant="outlined"
                                value={
                                    challengeViewModel.templateId
                                }
                                validators={['required']}
                                errorMessages={['This field is required']}
                                onChange={onChangeHandler}
                                fullWidth>
                                {scorecardViewModel!.templates
                                    .map(template =>
                                        <MenuItem
                                            id={template.name}
                                            value={template.id}>
                                            {template.name}
                                        </MenuItem>)}
                            </SelectValidator>
                        </Grid>
                        <Grid size={12} marginBottom={2}>
                            <SelectValidator
                                id="section"
                                name="sectionId"
                                label="Section"
                                variant="outlined"
                                value={
                                    challengeViewModel.sectionId
                                }
                                validators={['required']}
                                errorMessages={['This field is required']}
                                onChange={onChangeHandler}
                                fullWidth>
                                {scorecardViewModel!.templates.find(template => template.id === challengeViewModel.templateId)?.sections
                                    .map(section =>
                                        <MenuItem
                                            id={section.title}
                                            value={section.id}>
                                            {section.title}
                                        </MenuItem>)}
                            </SelectValidator>
                        </Grid>
                        <Grid size={12} marginBottom={2}>
                            <SelectValidator
                                id="question"
                                name="questionId"
                                label="Question"
                                variant="outlined"
                                value={challengeViewModel.questionId}
                                validators={['required']}
                                errorMessages={['This field is required']}
                                onChange={onChangeHandler}
                                fullWidth>
                                {scorecardViewModel!.templates.find(template => template.id === challengeViewModel.templateId)?.sections
                                    .find(section => section.id === challengeViewModel.sectionId)?.questions
                                    .map(question =>
                                        <MenuItem
                                            key={question.id}
                                            value={question.id}
                                            disabled={question.challenged}>
                                            {question.questionText}
                                        </MenuItem>)}
                            </SelectValidator>
                        </Grid>
                        {view === UserType.TeamLeader &&
                            <Grid size={12} marginBottom={2}>
                                <TextValidator
                                    id="agentCount"
                                    name="agentCount"
                                    label="Number Of Agents Affected"
                                    variant="outlined"
                                    validators={['required']}
                                    autoComplete='off'
                                    errorMessages={['This field is required']}
                                    value={challengeViewModel.agentCount}
                                    onChange={onChangeHandler}
                                    number
                                    fullWidth />
                            </Grid>
                        }
                    </Grid>
                    <Grid size={5.8}>
                        <Grid container justifyContent="space-between" alignItems="flex-start" margin={0} height="100%">
                            <Grid size={12}>
                                <Typography>Notes:</Typography>
                                <TextField
                                    id="note"
                                    onChange={noteOnChangeHandler}
                                    value={challengeViewModel.note}
                                    fullWidth
                                    multiline
                                    rows={7}
                                    placeholder="Add a note..."
                                    sx={{
                                        "& fieldset": { border: 'solid 1px rgb(192,192,192)', height: "206px" },
                                    }} />
                            </Grid>
                            <Grid size={12} sx={{ height: "calc(100% - 226px)", alignItems: "flex-end", display: "flex" }}>
                                <Grid container justifyContent="space-between" alignItems="flex-end" spacing={1} >
                                    <Grid size={6}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            fullWidth
                                            onClick={() => setOpenChallengeDialog(false)}>
                                            Cancel
                                        </Button>
                                    </Grid>
                                    <Grid size={6}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            size="small"
                                            disabled={loading}
                                            fullWidth>
                                            Challenge
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Fragment>
    )
}